import { Box } from '@mui/material';
import { PendingBankAccountItem } from 'hq/services/bank-account-service.types';
import { Table } from 'redesign';
import { useCallback, useMemo } from 'react';
import { useQueryClient } from 'react-query';
import { bankAccountAlerts, useAlert } from 'shared/components/alerts';
import { QUERY_KEYS } from 'api/query-keys';
import { useTranslation } from 'react-i18next';
import { getBankAccountTableData } from './utils/get-bank-account-table-data';
import { getBankAccountColumnDefinition } from './utils/get-bank-account-column-definition';
import { BankAccountTableData, BankAccountTableOptions } from './bank-account-table.types';
import { useApprovePendingBankAccount } from './hooks/use-approve-pending-bank-account';
import { useRejectPendingBankAccount } from './hooks/use-reject-pending-bank-account';

export const BankAccountTable = ({
  data,
  allowEmploymentId,
  onScroll,
}: {
  data: PendingBankAccountItem[];
  allowEmploymentId: boolean;
  onScroll?: (containerRefElement?: HTMLDivElement | null) => void;
}) => {
  const { alertError, alertSuccess } = useAlert();
  const queryClient = useQueryClient();
  const approveMutation = useApprovePendingBankAccount();
  const rejectMutation = useRejectPendingBankAccount();
  const { t } = useTranslation('hq-screen-specific');
  const columnDefinition = useMemo(
    () => getBankAccountColumnDefinition(allowEmploymentId, t),
    [allowEmploymentId, t]
  );
  const tableData = useMemo(() => getBankAccountTableData(data), [data]);

  const approve = useCallback(
    (id: string) => {
      approveMutation.mutate(id, {
        onSuccess: () => {
          alertSuccess(bankAccountAlerts.success.approved);
          queryClient.invalidateQueries(QUERY_KEYS.workers());
          queryClient.invalidateQueries(QUERY_KEYS.inbox());
          queryClient.invalidateQueries(QUERY_KEYS.notifications());
          queryClient.invalidateQueries(QUERY_KEYS.flexWorkerBankAccounts());
        },
        onError: (e) => {
          alertError(e);
        },
      });
    },
    [alertError, alertSuccess, approveMutation, queryClient]
  );

  const reject = useCallback(
    (id: string) => {
      rejectMutation.mutate(id, {
        onSuccess: () => {
          alertSuccess(bankAccountAlerts.success.reject);
          queryClient.invalidateQueries(QUERY_KEYS.workers());
          queryClient.invalidateQueries(QUERY_KEYS.inbox());
          queryClient.invalidateQueries(QUERY_KEYS.notifications());
          queryClient.invalidateQueries(QUERY_KEYS.flexWorkerBankAccounts());
        },
        onError: (e) => {
          alertError(e);
        },
      });
    },
    [alertError, alertSuccess, queryClient, rejectMutation]
  );

  return (
    <Box display="flex" minHeight={0}>
      <Table<BankAccountTableData, BankAccountTableOptions>
        stickyHeader
        columns={columnDefinition}
        data={tableData}
        tableStyle={{ tableLayout: 'fixed' }}
        approve={approve}
        reject={reject}
        onScroll={onScroll}
      />
    </Box>
  );
};
